// src/desktop/screens/auth/LoginScreen.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ScaleStage from '../../../shared/components/layout/ScaleStage';
import LoginView from '../../../shared/screens/auth/LoginView';
import { userApi } from '../../../api/axios';
import { useI18n } from '../../../i18n/I18nProvider';
import { clearSignupAgent } from '../../../shared/utils/signupAgent';
import { resolveAuthErrorMessage } from '../../../shared/utils/systemMessage';
import { markBrowserAuthSession, shouldResumeStoredSessionFromLogin } from '../../../shared/utils/authPersistence';
import ElectronTitleBar from '../../components/ElectronTitleBar';
import { getElectronTitleBarHeight } from '../../../config/runtime';

export default function DesktopLoginScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const titleBarHeight = getElectronTitleBarHeight();

  useEffect(() => {
    window.npcShell?.setWindowMode?.('login');

    // 이미 로그인 토큰이 살아있는 상태로 /login이 직접 열리면
    // 로그인 화면에 머물지 않고 루트 복구 플로우로 보낸다.
    // 루트 진입 후 recoveryTables/currentTableCode/lastTableCode 기준으로 기존 게임창을 복구한다.
    try {
      const token = localStorage.getItem('token');
      if (token && shouldResumeStoredSessionFromLogin()) {
        window.location.replace('/?recovery=1');
      }
    } catch (_) {
      // localStorage 접근 실패 시 로그인 화면을 그대로 유지한다.
    }
  }, [t]);

  const handleSubmit = useCallback(async ({ username, password, autoLogin }) => {
    setError('');
    setLoading(true);

    try {
      // ✅ 기존 로그인 기능 그대로 (POST /user/login)
      // 기존 LoginPage.jsx는 { id, password } 형태로 전송했으므로 동일하게 유지
      const res = await userApi.post('/login', { id: username, password });
      const { token, user } = res.data || {};

      if (!token) {
        throw new Error('TOKEN_MISSING');
      }

      // 토큰 및 유저 정보 저장
      localStorage.setItem('token', token);
      // ✅ 예전 테스트/구버전 잔여 키가 다른 계정 userCode로 fallback 되는 것을 차단
      localStorage.removeItem('user_code');
      localStorage.removeItem('userCode');
      if (user) {
        const uc = String(user.user_code || user.User_code || '').trim();
        const normalizedUser = { ...user, user_code: uc, User_code: uc };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
      }

      // 현재 브라우저 세션은 새로고침 동안 유지하고, 자동로그인은 선택한 경우에만 영구 허용한다.
      markBrowserAuthSession({ autoLogin: !!autoLogin });

      // ✅ 로그인 후 앱 진입
      // /login에서 reload하면 로그인 화면만 다시 뜰 수 있으므로 루트로 이동한다.
      // 루트 진입 시 recoveryTables/lastTableCode 복구 로직이 기존 게임창을 다시 연다.
      window.location.replace('/?recovery=1');
    } catch (err) {
      const msg = err?.message === 'TOKEN_MISSING'
        ? t('auth.login.error.tokenMissing', null, '로그인 응답에 토큰이 없습니다.')
        : resolveAuthErrorMessage(t, err?.response?.data || err, t('auth.login.error.failed', null, '로그인에 실패했습니다.'));
      setError(msg);
      // eslint-disable-next-line no-console
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleAccountRecovery = useCallback(() => {
    navigate('/account-recovery');
  }, [navigate]);

  const handleCreateAccount = useCallback(() => {
    clearSignupAgent();
    navigate('/signup/policy');
  }, [navigate]);

  return (
    <>
      <ElectronTitleBar
        channelLabel="Night Poker Club"
        centerTitle="Night Poker Club Login"
        showLogo={false}
        showChannelLabel={false}
        showMusicSection={false}
        showMusicButton={false}
        useNativeWindowControls={true}
      />

      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: titleBarHeight,
          bottom: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          background: '#000',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            background: '#000',
          }}
        >
          <source src="/assets/test.mp4" type="video/mp4" />
        </video>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 52% 42%, rgba(255,255,255,0.16), transparent 22%), linear-gradient(180deg, rgba(0,0,0,0.16) 0%, rgba(0,0,0,0.32) 52%, rgba(0,0,0,0.72) 100%), linear-gradient(90deg, rgba(0,0,0,0.54) 0%, rgba(0,0,0,0.06) 45%, rgba(0,0,0,0.58) 100%), rgba(0,0,0,0.14)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.16) 1px, transparent 1px)',
            backgroundSize: '3px 3px',
            opacity: 0.45,
            mixBlendMode: 'multiply',
          }}
        />
      </div>

      {/* 간단 에러 배너(디자인은 나중에 커스텀) */}
      {error && (
        <div
          style={{
            position: 'fixed',
            top: titleBarHeight + 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            padding: '10px 14px',
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid #ff6b6b',
            color: '#ff6b6b',
            fontSize: 14,
            maxWidth: 340,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <ScaleStage
        baseWidth={375}
        baseHeight={812}
        outerBackground="transparent"
        maxScale={2.4}
        padding={0}
        topInset={titleBarHeight}
      >
        <LoginView
          renderVideoBackground={false}
          onSubmit={handleSubmit}
          onAccountRecovery={handleAccountRecovery}
          onCreateAccount={handleCreateAccount}
          loading={loading}
        />
      </ScaleStage>
    </>
  );
}
